import type { Position } from '@rio-cloud/rio-uikit/mapTypes';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { CoordinateInput } from '#components/form/CoordinateInput.tsx';
import { ControlledFormInput } from '#components/form/FormInput.tsx';
import AddressForm from '#components/order/CreateOrderDialog/AddressForm.tsx';
import { useDebouncedGeocoding } from '#components/order/CreateOrderDialog/useDebouncedGeocoding.tsx';

import { DraggableLocationMap } from '../../shared/DraggableLocationMap.tsx';
import { ControlledAddressAutocomplete } from './ControlledAddressAutocomplete.tsx';
import { ControlledCommentInput } from './ControlledCommentInput.tsx';
import { Coordinates } from './Coordinates.tsx';
import type { Location } from './OrderFormValidation.ts';
import { TimeWindowForm } from './TimeWindowForm.tsx';

type LocationFormType = 'order-modal' | 'import-modal';
export type AddressTypes = 'loadingStop' | 'unloadingStop' | 'executionStop';

type LocationFormProps = {
    addressType: AddressTypes;
    minDate?: Date;
    locationPrefix?: `orders.${number}`;
    type?: LocationFormType;
};

export const LocationForm = ({ addressType, minDate, locationPrefix, type = 'order-modal' }: LocationFormProps) => {
    const prefix = locationPrefix ? `${locationPrefix}.${addressType}` : addressType;
    return (
        <LocationFormView
            addressType={addressType}
            addressDisabled={type === 'order-modal'}
            commentName={commentField(addressType, locationPrefix)}
            coordinatesPrefix={prefix}
            formType={type}
            key={`location-form${addressType}`}
            minDate={minDate}
            prefix={prefix}
            showCommentInitially={type === 'import-modal'}
        />
    );
};

type LocationFormViewProps = {
    addressDisabled?: boolean;
    addressType: AddressTypes;
    allowPastDates?: boolean;
    commentName: string;
    coordinatesPrefix?: string;
    formType?: LocationFormType;
    minDate?: Date;
    prefix?: string;
    showCommentInitially?: boolean;
    showCompanyInput?: boolean;
    companyNameField?: string;
};

const fieldName = (prefix: string | undefined, field: string) => (prefix ? `${prefix}.${field}` : field);

export const LocationFormView = ({
    addressDisabled = false,
    addressType,
    allowPastDates = false,
    commentName,
    coordinatesPrefix,
    formType = 'order-modal',
    minDate,
    prefix,
    showCommentInitially = false,
    showCompanyInput = true,
    companyNameField,
}: LocationFormViewProps) => {
    const intl = useIntl();
    // biome-ignore lint/suspicious/noExplicitAny: shared form view supports order, import, and stop-edit form shapes.
    const { trigger, watch, setValue, control } = useFormContext<any>();
    const [addressPosition, setAddressPosition] = useState<Position | undefined>(undefined);

    const latitudeField = fieldName(prefix, 'latitude');
    const longitudeField = fieldName(prefix, 'longitude');
    const locationField = fieldName(prefix, 'location');
    const nameField = companyNameField ?? fieldName(prefix, 'name');

    const lat = watch(latitudeField);
    const lng = watch(longitudeField);

    const location: Location = watch(locationField);

    const isMarker =
        lat !== undefined && lng !== undefined && (lat !== location.latitude || lng !== location.longitude);
    const isMarkerRef = useRef(isMarker);
    isMarkerRef.current = isMarker;

    const onUpdateAddressPosition = (position: Position | undefined) => {
        setAddressPosition(position);
        if (position) {
            setValue(`${locationField}.latitude`, position.lat);
            setValue(`${locationField}.longitude`, position.lng);

            if (!isMarkerRef.current) {
                setCoordinates(position.lat, position.lng);
            }
        }
    };

    useDebouncedGeocoding({ setPosition: onUpdateAddressPosition, location });

    const setCoordinates = (latitude: number, longitude: number) => {
        setValue(latitudeField, latitude);
        setValue(longitudeField, longitude);
        trigger(latitudeField).catch(console.warn);
        trigger(longitudeField).catch(console.warn);
    };

    const removeCoordinates = () => {
        setValue(latitudeField, location.latitude ?? undefined);
        setValue(longitudeField, location.longitude ?? undefined);
        trigger(latitudeField).catch(console.warn);
        trigger(longitudeField).catch(console.warn);
    };

    const triggerPositionUpdate = async (location: Location) => {
        await trigger(`${locationField}.street`);
        await trigger(`${locationField}.city`);
        await trigger(`${locationField}.country`);
        await trigger(`${locationField}.zipCode`);

        if (!isMarkerRef.current && location.latitude !== undefined && location.longitude !== undefined) {
            setCoordinates(location.latitude, location.longitude);
        }
    };

    return (
        <div>
            <div className={`display-grid ${formType === 'order-modal' ? 'grid-cols-2' : 'grid-cols-1'} gap-20`}>
                <div className='position-relative display-flex flex-column'>
                    <div className='display-flex flex-column row-gap-5'>
                        {formType === 'order-modal' && (
                            <ControlledAddressAutocomplete
                                name={locationField}
                                companyName={nameField}
                                control={control}
                                triggerPositionUpdate={triggerPositionUpdate}
                                formGroupClassName={'margin-bottom-0'}
                            />
                        )}
                        {showCompanyInput && (
                            <ControlledFormInput
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.company',

                                    defaultMessage: 'Company',
                                })}
                                control={control}
                                name={nameField}
                                rioglyph={'building'}
                                formGroupClassName={'margin-bottom-0'}
                            />
                        )}
                        <AddressForm addressPrefix={prefix} disabled={addressDisabled} />
                        {formType === 'import-modal' && (
                            <div className='display-grid grid-cols-2 gap-10'>
                                <CoordinateInput
                                    name={latitudeField}
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.common.coordinates.latitude',

                                        defaultMessage: 'Latitude',
                                    })}
                                />
                                <CoordinateInput
                                    name={longitudeField}
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.common.coordinates.longitude',

                                        defaultMessage: 'Longitude',
                                    })}
                                />
                            </div>
                        )}
                        <TimeWindowForm allowPastDates={allowPastDates} prefix={prefix} minDate={minDate} />
                    </div>
                </div>
                {formType === 'order-modal' && (
                    <div className='height-100pct min-height-250 display-flex flex-column'>
                        <Coordinates
                            marker={isMarker}
                            onClick={removeCoordinates}
                            addressType={addressType}
                            coordinatesPrefix={coordinatesPrefix}
                        />
                        <div
                            className={
                                'position-relative flex-1-1 rounded overflow-hidden  margin-bottom-0 shadow-accent margin-top-0'
                            }
                        >
                            <DraggableLocationMap
                                mapPosition={lat !== undefined && lng !== undefined ? { lat, lng } : undefined}
                                addressPosition={addressPosition}
                                onPositionChange={position => {
                                    setCoordinates(position.lat, position.lng);
                                }}
                                showMarker={isMarker}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ControlledCommentInput
                name={commentName}
                control={control}
                intl={intl}
                className={'margin-top-15'}
                showInitially={showCommentInitially}
            />
        </div>
    );
};

const commentField = (addressType: AddressTypes, locationPrefix?: string) => {
    const prefix = locationPrefix ? `${locationPrefix}.` : '';
    switch (addressType) {
        case 'loadingStop':
            return `${prefix}loadingComment`;
        case 'unloadingStop':
            return `${prefix}unloadingComment`;
        case 'executionStop':
            return `${prefix}executionComment`;
    }
};
